import { Button, Icon, SectionCard } from '../../../components/ui';
import { formatBRLFromCents, formatCount } from '../../../lib/format';
import type { Segment, SegmentKey } from '../types';
import { SEGMENT_FAMILY_ICON, SEGMENT_FAMILY_LABEL, SEGMENT_FAMILY_ORDER, SEGMENT_SHORT_LABEL } from '../vocabulario';

/**
 * "Parado agora": quem ja passou pelo fato e ainda nao voltou, por familia,
 * com o valor parado e um atalho para preparar o envio do publico mais
 * valioso de cada familia.
 *
 * E retrato de agora (fora do filtro de periodo) e so conta publico
 * pos-fato. Sem nada parado, o vazio diz que e boa noticia.
 */
export function ParadoAgora({ segments, onPreparar, className = '' }: { readonly segments: readonly Segment[]; readonly onPreparar: (segmento: SegmentKey) => void; readonly className?: string }): JSX.Element {
  const familias = SEGMENT_FAMILY_ORDER.map((familia) => {
    const lista = segments.filter((s) => s.family === familia && s.estimatedCount > 0);
    const melhor = [...lista].sort((a, b) => b.stalledAmountCents - a.stalledAmountCents)[0] ?? null;
    return {
      familia,
      contatos: lista.reduce((acc, s) => acc + s.estimatedCount, 0),
      parado: lista.reduce((acc, s) => acc + s.stalledAmountCents, 0),
      melhor,
    };
  }).filter((f) => f.contatos > 0);
  const total = familias.reduce((acc, f) => acc + f.parado, 0);
  const contatos = familias.reduce((acc, f) => acc + f.contatos, 0);

  return (
    <SectionCard id="parado-agora" icon="relogio" title="Parado agora" description="Quem já abandonou, teve o pagamento recusado ou venceu e não voltou. Retrato de agora, fora do filtro de período." className={className}>
      {familias.length === 0 ? (
        <p className="rounded-xl border border-dashed border-edge bg-elevated px-4 py-6 text-center text-sm text-ink-muted">Ninguém parado: todo mundo que abandonou ou venceu já voltou ou está na régua automática.</p>
      ) : (
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-3xl font-semibold tracking-tight text-ink">{formatBRLFromCents(total)}</p>
            <p className="text-xs text-ink-subtle">parados em {formatCount(contatos)} contatos (um contato pode estar em dois públicos)</p>
          </div>
          <ul className="flex flex-col gap-2">
            {familias.map((f) => (
              <li key={f.familia} className="flex items-center justify-between gap-3 rounded-lg border border-line bg-elevated px-3 py-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  <span aria-hidden="true" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand/10 text-brand">
                    <Icon name={SEGMENT_FAMILY_ICON[f.familia]} size="sm" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ink">{SEGMENT_FAMILY_LABEL[f.familia]}</p>
                    <p className="text-xs tabular-nums text-ink-subtle">
                      {formatCount(f.contatos)} contatos · {formatBRLFromCents(f.parado)}
                    </p>
                  </div>
                </div>
                {f.melhor === null ? null : (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconRight="seta-dir"
                    onClick={() => onPreparar(f.melhor?.key ?? 'abandonaram_sem_pagar')}
                    title={`Preparar envio para ${SEGMENT_SHORT_LABEL[f.melhor.key]}`}
                    aria-label={`Preparar envio para ${SEGMENT_SHORT_LABEL[f.melhor.key]}`}
                  >
                    Preparar
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}
